export const supportedLocales = ['ru', 'en'] as const;

export type AppLocale = (typeof supportedLocales)[number];

export const localeTags: Record<AppLocale, string> = {
  ru: 'ru-RU',
  en: 'en-US',
};

export const defaultLocale: AppLocale = 'ru';
export const defaultToolId = 'password-generator' as const;

export const toolRegistry = [
  {
    id: 'password-generator',
    translationKey: 'passwordGenerator',
    icon: 'fas fa-key',
    category: 'core',
  },
  {
    id: 'char-counter',
    translationKey: 'characterCounter',
    icon: 'fas fa-font',
    category: 'core',
  },
  {
    id: 'special-space',
    translationKey: 'htmlSpaces',
    icon: 'fas fa-grip-lines',
    category: 'core',
  },
  {
    id: 'encoding-converter',
    translationKey: 'encodingConverter',
    icon: 'fas fa-exchange-alt',
    category: 'core',
  },
  {
    id: 'base64',
    translationKey: 'base64',
    icon: 'fas fa-code',
    category: 'core',
  },
  {
    id: 'json-formatter',
    translationKey: 'jsonFormatter',
    icon: 'fas fa-align-center',
    category: 'formatters',
  },
  {
    id: 'color-converter',
    translationKey: 'colorConverter',
    icon: 'fas fa-palette',
    category: 'formatters',
  },
  {
    id: 'url-encoder',
    translationKey: 'urlTool',
    icon: 'fas fa-globe',
    category: 'formatters',
  },
  {
    id: 'html-entities',
    translationKey: 'htmlEntities',
    icon: 'fas fa-code',
    category: 'formatters',
  },
  {
    id: 'iphone-countdown',
    translationKey: 'iphoneCountdown',
    icon: 'fas fa-clock',
    category: 'misc',
  },
  {
    id: 'pomodoro',
    translationKey: 'pomodoro',
    icon: 'fas fa-stopwatch',
    category: 'misc',
  },
] as const;

export type ToolDefinition = (typeof toolRegistry)[number];
export type ToolId = ToolDefinition['id'];

export const toolCategories = [
  {
    id: 'core',
    translationKey: 'core',
  },
  {
    id: 'formatters',
    translationKey: 'formatters',
  },
  {
    id: 'misc',
    translationKey: 'misc',
  },
] as const;

export const encodingOptions = [
  'windows-1251',
  'iso-8859-1',
  'koi8-r',
  'iso-8859-5',
  'utf-8',
  'utf-16',
  'ibm866',
  'mac',
] as const;

export const htmlSpaceOptions = [
  { id: 'nbsp', entity: '&nbsp;', preview: '\u00A0' },
  { id: 'ensp', entity: '&ensp;', preview: '\u2002' },
  { id: 'emsp', entity: '&emsp;', preview: '\u2003' },
  { id: 'thinsp', entity: '&thinsp;', preview: '\u2009' },
  { id: 'zwsp', entity: '&#8203;', preview: '\u200B' },
  { id: 'ndash', entity: '&ndash;', preview: '\u2013' },
  { id: 'mdash', entity: '&mdash;', preview: '\u2014' },
] as const;

// Social links are temporarily hidden in the footer (block is commented out in
// Footer.tsx). Restore both together when official accounts are ready.
export const socialLinks = [
  {
    id: 'github',
    href: 'https://github.com/devdom',
    icon: 'fab fa-github',
    label: 'GitHub',
  },
  {
    id: 'twitter',
    href: 'https://twitter.com/devdom',
    icon: 'fab fa-twitter',
    label: 'Twitter',
  },
  {
    id: 'telegram',
    href: 'https://t.me/devdom',
    icon: 'fab fa-telegram',
    label: 'Telegram',
  },
] as const;

// Contact form lives on the DEVDOM studio website (working form anchor).
export const contactFormUrl = 'https://devdom.ru/#contact-form';

export const footerLinks = [
  { id: 'about' },
  { id: 'privacy' },
  { id: 'contact' },
] as const;

export const staticPageIds = ['about', 'privacy'] as const;
export type StaticPageId = (typeof staticPageIds)[number];

export const isStaticPageId = (
  value: string | null | undefined
): value is StaticPageId =>
  (staticPageIds as readonly string[]).includes(value ?? '');

const toolIds = new Set<string>(toolRegistry.map(tool => tool.id));

export const isSupportedLocale = (
  value: string | undefined
): value is AppLocale => supportedLocales.includes((value ?? '') as AppLocale);

export const isToolId = (value: string | null | undefined): value is ToolId =>
  toolIds.has(value ?? '');

export const getToolById = (toolId: ToolId) =>
  toolRegistry.find(tool => tool.id === toolId) ?? toolRegistry[0];
